import {AppState} from './../../../store';
import {createSelector} from 'reselect';
import {
  hotelIdSelectorFnFromCommon,
  checkinSelectorFn,
  checkoutSelectorFn,
  roomStringSelectorFn,
  countrySelectorFn,
  goingForwardPropertyIdSelector,
} from '@stencil/hotels-common-reducer/src/selectors';

import parseDateIntoHermesString from '@stencil/hotels-utils/src/date-utils/parse-date-into-hermes-string';
import getSlashAndShowPrice from '@stencil/hotels-utils/src/get-details-slash-and-show-price';

import {DetailStaticShape} from './types';
import {RplType} from '@stencil/hotels-types/src/details-types';

export const detailSelector = (state: AppState) => state.details;
export const cityNameFromParamsSelector = createSelector(
  detailSelector,
  details => details.cityName,
);
export const hermesDataSelector = createSelector(detailSelector, details => details?.hermesData);

export const openCategorizedAmenitiesSelector = createSelector(
  detailSelector,
  detailsData => detailsData.openCategorizedAmenities,
);
export const allStaticDataSelector = createSelector(
  detailSelector,
  details => details.staticData || ({} as DetailStaticShape),
);

export const mapDataSelector = createSelector(allStaticDataSelector, staticData => {
  return {
    la: staticData.la,
    lo: staticData.lo,
    mapImageUrlData: staticData.map_marker,
  };
});

export const selectedRoomSelector = createSelector(
  detailSelector,
  details => details?.selectedRoom || {},
);

export const selectedRoomImagesSelector = createSelector(
  selectedRoomSelector,
  selectedRoom => selectedRoom.roomImages || [],
);

export const selectedRoomTypeNameSelector = createSelector(
  selectedRoomSelector,
  selectedRoom => selectedRoom.roomName || '',
)

export const detailUrlFromStaticData = createSelector(
  allStaticDataSelector,
  staticData => staticData?.hotel_url || '',
);

export const aboutProperty = createSelector(
  detailSelector,
  details => details.processedAboutPropertyData || '',
);

export const fetchStaticDataStatusSelector = createSelector(
  detailSelector,
  details => details?.fetchStaticDataStatus,
);

//Checkin and checkout time
export const checkinTime = createSelector(
  allStaticDataSelector,
  staticData => staticData.check_in || '',
);

export const formattedCheckinTimeSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData.check_in_formatted,
);

export const formattedCheckoutTimeSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData.check_out_formatted,
);

export const staticDataMetaConfig = createSelector(
  allStaticDataSelector,
  staticData => staticData?.meta_config || {},
);

export const metaAmenitiesDataSelector = createSelector(
  staticDataMetaConfig,
  staticDataMetaConfig => staticDataMetaConfig?.amenities_data || {},
);

export const checkoutTime = createSelector(
  allStaticDataSelector,
  staticData => staticData.check_out || '',
);

//Formatted Checkin and checkout time
export const checkinFormattedTimeFromStaticData = createSelector(
  allStaticDataSelector,
  staticData => staticData.check_in_formatted || '',
);
export const checkoutFormattedTimeFromStaticData = createSelector(
  allStaticDataSelector,
  staticData => staticData.check_out_formatted || '',
);

export const staticDataStatusSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.status || {},
);

export const propertyTypeSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.ht?.[0] || 'Hotel',
);

export const detailHttpCode = createSelector(
  staticDataStatusSelector,
  statusData => statusData?.http_code || 200,
);

export const isStaticDataLoading = createSelector(
  detailSelector,
  details => details.isStaticDataLoading,
);
export const hasStaticDataSelector = createSelector(
  detailSelector,
  details => details.hasStaticData,
);

export const hotelPolicyData = createSelector(detailSelector, details => details.hotelPolicyData);
export const hotelPolicyFetchStatusSelector = createSelector(
  hotelPolicyData,
  data => data.hotelPolicyFetchStatus,
);

export const hotelIdSelectorFnFromDetails = createSelector(
  detailSelector,
  detailsData => detailsData.vhid || '',
);

const mapMarkerSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData.map_marker || {},
);
export const locationMapImageSelectorFn = createSelector(
  mapMarkerSelector,
  mapMarkerData => mapMarkerData.desktop,
);

export const latLongSelectorFn = createSelector(allStaticDataSelector, staticData => {
  const la = staticData?.la;
  const lo = staticData?.lo;

  if (la && lo) {
    return {
      la,
      lo,
    };
  }

  return {};
});

export const pointOfInterestsSelectorFn = createSelector(
  allStaticDataSelector,
  staticData => staticData?.poi_data || [],
);

export const pointOfInterestsDataSelector = createSelector(
  [pointOfInterestsSelectorFn],
  pointOfInterests => pointOfInterests,
);

export const cityId = createSelector(allStaticDataSelector, staticData => staticData?.vcid);
export const cityIdSelector = createSelector(cityId, cityId => cityId);

export const countryNameSelector = createSelector(
  allStaticDataSelector,
  allStaticData => allStaticData?.cn || 'Country',
);
export const seoMarkupDataSelector = createSelector(
  allStaticDataSelector,
  allStaticData => allStaticData?.seo_markup_data || ({} as DetailStaticShape['seo_markup_data']),
);

export const hotelCityURLSelectorFn = createSelector(
  seoMarkupDataSelector,
  seoMarkupData => seoMarkupData?.hotel_city_url,
);
export const countryUrlSelector = createSelector(
  seoMarkupDataSelector,
  seoMarkupData => seoMarkupData?.country_url,
);

export const hotelCityURLSelector = createSelector(
  hotelCityURLSelectorFn,
  hotelCityURL => hotelCityURL,
);

export const goStaysSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.ty === 'gostays',
);
export const hotelRatingSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.hr,
);
export const hotelName = createSelector(allStaticDataSelector, staticData => staticData?.hn);
export const hotelAddressSelectorFn = createSelector(
  allStaticDataSelector,
  staticData => staticData?.address,
);
export const hotelArea = createSelector(allStaticDataSelector, staticData => staticData?.c);
export const cityNameSelector = createSelector(hotelArea, cityName => cityName);
export const hotelCountrySelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.cn,
);
export const hotelAreaPincodeSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.pincode,
);
export const hotelStaticRatingSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.gr,
);
export const hotelStaticReviewsNumberSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.gir_rcnt,
);
export const hotelRatingSourceSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.gri,
);
export const hotelStaticMinPrice = createSelector(
  allStaticDataSelector,
  staticData => staticData?.minprice,
);
export const ugcSentiments = createSelector(
  allStaticDataSelector,
  staticData => staticData?.ugc_sentiments,
);

// 🐨 profile – this selector will return a new reference everytime. Remember to use shallow equals
//whenever this selector is used
export const indexedRoomsStaticDataSelectorFn = createSelector(
  allStaticDataSelector,
  staticData => {
    const roomInfo = staticData?.room_info || [];

    type IndexedRoomInfoType = {
      [key: string]: typeof roomInfo[0];
    };

    let indexedRoomInfo: IndexedRoomInfoType = {};

    roomInfo.forEach(room => {
      if (room?.type_code) {
        indexedRoomInfo[room.type_code] = room;
      }

      if (room.type_codes && Array.isArray(room.type_codes)) {
        room.type_codes.forEach(code => {
          indexedRoomInfo[code] = room;
        });
      }
    });

    return indexedRoomInfo;
  },
);

export const indexedRoomsStaticDataSelector = createSelector(
  indexedRoomsStaticDataSelectorFn,
  indexedRoomsStaticData => indexedRoomsStaticData,
);

export const hotelierGallery = createSelector(
  allStaticDataSelector,
  staticData => staticData?.gallery || [],
);

export const formattedHotelierGallerySelector = createSelector(hotelierGallery, data => {
  var hotelierImages = data.map(function(item) {
    return {
      imgSrc: item?.[2],
      mediumUrl: item?.[1],
      overlayData: {
        tag: item?.[5],
      },
    };
  });
  return hotelierImages || [];
});

export const travellerGallery = createSelector(
  allStaticDataSelector,
  staticData => staticData?.ugc_images || [],
);

export const formattedTravellerGallerySelector = createSelector(travellerGallery, data => {
  var travellerImages = data.map(function(item) {
    return {
      imgSrc: item?.b || item.imgSrc,
      thumbnailSrc: item?.s || item.thumbnailSrc,
      mediumUrl: item?.m || item.mediumUrl,
      overlayData: {
        tag: item.t || item.overlayData?.tag,
      },
    };
  });
  return travellerImages;
});

export const captivateGallery = createSelector(
  allStaticDataSelector,
  staticData => staticData?.gallery_v2 || [],
);

export const formattedCaptivateGallery = createSelector(captivateGallery, data => {
  var captivateImages = data.map(item => {
    return {
      imgSrc: item?.b,
      src: item?.src,
      mediumUrl: item?.m,
      overlayData: {
        tag: item?.tag,
      },
    };
  });
  return captivateImages;
});

const girDataSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.gir_data || {},
);
export const totalTravellerImages = createSelector(
  girDataSelector,
  girData => girData?.approved_img_cnt || 0,
);

export const hotelAmenitiesSelectorFn = createSelector(
  allStaticDataSelector,
  staticData =>
    staticData?.facilities || {
      all: [],
      cmapped: [],
      categorized: [],
    },
);

export const hotelAmenitiesSelector = createSelector(hotelAmenitiesSelectorFn, amenitiesObject => {
  return amenitiesObject?.cmapped || [];
});

export const categorizedAmenitiesSelector = createSelector(
  hotelAmenitiesSelectorFn,
  amenitiesObject => {
    return amenitiesObject?.categorized || [];
  },
);

export const hotelsAmenitiesSelector = createSelector(
  hotelAmenitiesSelectorFn,
  hotelAmenities => hotelAmenities,
);

export const roomsStaticData = createSelector(
  allStaticDataSelector,
  staticData => staticData?.room_info || [],
);

// ---------------------------- HERMES DATA ---------------------------------

export const hasHermesDataBooleanSelector = createSelector(
  detailSelector,
  detailsData => detailsData.hasHermesData,
);

export const detailsHasDataSelector = createSelector(
  hasStaticDataSelector,
  hasHermesDataBooleanSelector,
  (hasStaticData, hasHermesData) => hasHermesData || hasStaticData,
);

export const metaInfoSelector = createSelector(
  hermesDataSelector,
  hermesData => hermesData.meta_info || {},
);

export const bnplPersuasionSelector = createSelector(metaInfoSelector, metaInfo => ({
  bnplCallout: metaInfo?.rnplper?.per || '',
  bnplDetails: metaInfo?.rnplper?.dt || '',
}));

export const metaFlagsSelector = createSelector(
  metaInfoSelector,
  metaInfo => metaInfo?.flags || {},
);

export const showPaxSplittedRoomStringSelector = createSelector(
  metaFlagsSelector,
  flags => flags?.pxgrn,
);
export const paxSplittedRoomStringSelector = createSelector(
  metaInfoSelector,
  metaInfo => metaInfo?.altpx,
);

export const paxSplittedDataSelector = createSelector(
  [showPaxSplittedRoomStringSelector, paxSplittedRoomStringSelector],
  (showPaxSplittedRoomString, paxSplittedRoomStringSelector) => {
    if (showPaxSplittedRoomString) {
      return paxSplittedRoomStringSelector;
    }

    return false;
  },
);

export const hermesDataObjectSelector = createSelector(
  hermesDataSelector,
  hermesData => hermesData.data || {},
);
export const hotelPersuasionSelector = createSelector(
  hermesDataObjectSelector,
  hermesDataObject => hermesDataObject.tp || [],
);

export const isHermesLoading = createSelector(
  detailSelector,
  detailsData => detailsData.isHermesDataLoading,
);

export const roomsDataHermes = createSelector(
  hermesDataObjectSelector,
  hermesDataObject => hermesDataObject?.reg || [],
);

export const noOfRoomTypesSelector = createSelector(
  roomsDataHermes,
  roomsDataFromHermes => roomsDataFromHermes.length,
);

export const noOfRoomsSelector = createSelector(roomsDataHermes, roomsDataFromHermes => {
  let noOfRooms = 0;

  roomsDataFromHermes.forEach(roomType => {
    noOfRooms += roomType.rpl.length;
  });

  return noOfRooms;
});

export const roomDataHermesSelector = createSelector(
  roomsDataHermes,
  roomsDataHermes => roomsDataHermes,
);

export const firstRoomPlanDataSelector = createSelector(
  roomDataHermesSelector,
  roomsDataHermes => roomsDataHermes[0]?.rpl[0] || ({} as RplType),
);

export const noOfOffersTextSelector = createSelector(
  firstRoomPlanDataSelector,
  firstRoomFlavorData => firstRoomFlavorData?.pd?.ofs || '',
);

export const firstRoomFCPDataSelector = createSelector(
  firstRoomPlanDataSelector,
  firstRoomFlavorData => firstRoomFlavorData?.fcp || {},
);

export const showAndSlashPriceForFirstRoomPlanSelector = createSelector(
  firstRoomPlanDataSelector,
  firstRoomFlavorData => {
    return getSlashAndShowPrice(firstRoomFlavorData);
  },
);

export const pphSelector = createSelector(
  firstRoomPlanDataSelector,
  firstRoomFlavorData => firstRoomFlavorData?.pd?.pph || '',
);

export const earlyCheckinAvailableSelector = createSelector(
  [metaInfoSelector, firstRoomPlanDataSelector],
  (metaInfo, firstRoomPlanData) => {
    const {aoi = []} = firstRoomPlanData || {};

    const {aocode = []} = metaInfo;
    const ecIndex = aocode.indexOf('ec');
    const earlyCheckinAvailable = !!(ecIndex > -1 && aoi.indexOf(ecIndex) > -1);

    return earlyCheckinAvailable;
  },
);

export const freeCancellationPolicyDataForDetailsPageSelector = createSelector(
  firstRoomPlanDataSelector,
  firstRoomPlanData => {
    return firstRoomPlanData.fcp;
  },
);

// needed for hotel policy url
export const oldPaxSelector = createSelector(
  roomDataHermesSelector,
  roomsDataHermes => roomsDataHermes[0]?.rpl[0]?.pd?.pax,
);

// export const roomsFilterDataFn= (state: AppState) => state.details.hermesData.meta_info?.rf || [];
export const roomsFilterDataSelector = createSelector(
  metaInfoSelector,
  metaInfo => metaInfo?.rf || [],
);

export const countryCode = createSelector(metaInfoSelector, metaInfo => metaInfo?.coc || '');

export const showHotelPolicyFromHermesSelectorFn = createSelector(
  metaFlagsSelector,
  flags => !!flags?.is_hp,
);

export const hotelPolicyFromHermesSelectorFn = createSelector(
  metaInfoSelector,
  metaInfo => metaInfo?.hp,
);

export const hotelPolicyFromHermesSelector = createSelector(
  [showHotelPolicyFromHermesSelectorFn, hotelPolicyFromHermesSelectorFn],
  (showHotelPolicyFromHermes, hotelPolicyListFromHermes) => ({
    showHotelPolicyFromHermes,
    hotelPolicyListFromHermes,
  }),
);

export const shouldShowHermesPolicySelector = createSelector(
  hotelPolicyFromHermesSelector,
  ({showHotelPolicyFromHermes, hotelPolicyListFromHermes}) =>
    showHotelPolicyFromHermes &&
    hotelPolicyListFromHermes &&
    Array.isArray(hotelPolicyListFromHermes) &&
    hotelPolicyListFromHermes.length,
);

export const similarHotelsSelectorFn = createSelector(
  detailSelector,
  detailsData => detailsData.similarHotels,
);

export const similarHotelsSelector = createSelector(
  similarHotelsSelectorFn,
  similarHotelsData => similarHotelsData,
);

export const coupleFriendlyFlagSelectorFn = createSelector(metaFlagsSelector, flags => !!flags?.cf);

export const gostayFlagSelectorFn = createSelector(metaFlagsSelector, flags => !!flags?.gostay);

export const gostaysHoverDataSelectorFn = createSelector(
  metaInfoSelector,
  metaInfo => metaInfo?.gsm || [],
);

export const coupleFriendlyDataSelectorFn = createSelector(
  hermesDataObjectSelector,
  hermesDataObject => hermesDataObject.tlp || [],
);

export const mixedPersuasions = createSelector(metaInfoSelector, metaInfo => {
  const {per = {}} = metaInfo || {};
  return per;
});

export const hotelAddressSelector = createSelector(
  [hotelAddressSelectorFn, hotelArea, hotelCountrySelector, hotelAreaPincodeSelector],
  (hotelAddressText, hotelArea, hotelCountry, hotelAreaPincode) => ({
    hotelAddressText,
    hotelArea,
    hotelCountry,
    hotelAreaPincode,
  }),
);

export const finalAddressSelector = createSelector(
  allStaticDataSelector,
  staticData => staticData?.final_address,
);

export const detailsStaticDataSelector = createSelector(
  hotelName,
  hotelAddressSelector,
  hotelRatingSelector,
  hotelStaticRatingSelector,
  hotelStaticReviewsNumberSelector,
  (detailsHotelName, detailsHotelAddressData, detailsHotelStars, detailsRating, reviewNumbers) => ({
    detailsHotelName,
    detailsHotelAddressData,
    detailsHotelStars,
    detailsRating,
    reviewNumbers,
  }),
);

export const socialPersuasionDataSelectorFn = createSelector(
  hermesDataObjectSelector,
  hermesDataObject => hermesDataObject.sp,
);

export const socialPersuasionDataSelector = createSelector(
  socialPersuasionDataSelectorFn,
  socialPersuasionData => socialPersuasionData,
);

export const locationPersuasionsFromHermesSelector = createSelector(metaInfoSelector, metaInfo => {
  const {ptl = {}, ptk = ''} = metaInfo || {};

  const dealsAndPersuasions = ptl[ptk] || [];

  return dealsAndPersuasions.filter(item => item.pid === 'spotLocVoy');
});

// const dealsDistinguisher = ['dealsHotelOfferText', 'spotDeal'];

// const deals =
//   (dealsAndPersuasions &&
//     dealsAndPersuasions.filter(
//       persuasionOrDeal =>
//         persuasionOrDeal.pid &&
//         dealsDistinguisher.includes(persuasionOrDeal.pid)
//     )) ||
//   [];

const uspDataSelector = createSelector(allStaticDataSelector, staticData => staticData?.usp || {});
export const locationPersuasions = createSelector(uspDataSelector, uspData => uspData?.loc);

export const freeBreakfastOrCancellationPersuasionAndAdditionalPersuasionSelector = createSelector(
  hermesDataObjectSelector,
  hermesDataObject => {
    return {
      breakFastOrCancellation: hermesDataObject?.cp2 || [],
      additionalPersuasion: hermesDataObject?.cp3 || [],
      dummyPersuasions: hermesDataObject?.cp1 || [],
    };
  },
);

export const uiStateSelector = createSelector(detailSelector, detailsData => detailsData.UI);

export const currentRoomData = createSelector(
  uiStateSelector,
  uiStateData => uiStateData?.currentRoomData,
);

export const offerCodeSelector = createSelector(detailSelector, details => {
  return details?.offerCodeFromQueryParam;
});

// export const hotelIdSelector = createSelector(
//   [hotelIdSelectorFnFromDetails, hotelIdSelectorFnFromCommon],
//   (hotelIdFromDetails, hotelIdFromCommon) => {
//     return hotelIdFromDetails || hotelIdFromCommon;
//   },
// );

export const hotelIdSelector = goingForwardPropertyIdSelector;

export const selectedHotelStatic = createSelector(
  detailSelector,
  details => details?.selectedHotelStatic || {},
);

export const hotelNameSelectedHotelStaticSelector = createSelector(
  selectedHotelStatic,
  selectedHotelStatic => selectedHotelStatic?.hn,
);

export const hotelPolicyDataSelector = createSelector(
  hotelPolicyData,
  hotelPolicyData => hotelPolicyData,
);

export const hotelPolicyAdditionalDataSelector = createSelector(
  hotelPolicyData,
  hotelPolicyData => {
    const additionalHotelPoliciesDataObj = hotelPolicyData.hotelPolicyAdditionalData || [];

    const flattenedAdditionalHotelPoliciesList = Object.keys(additionalHotelPoliciesDataObj).reduce(
      (acc, nextKey) => {
        const annoucementsForKey = additionalHotelPoliciesDataObj[nextKey] || [];
        return [...acc, ...annoucementsForKey];
      },
      [],
    );

    return flattenedAdditionalHotelPoliciesList;
  },
);

export const detailHttpCodeSelector = createSelector(detailHttpCode, detailHttpCode =>
  detailHttpCode.toString(),
);

export const detailUrlFromStaticDataSelector = createSelector(
  detailUrlFromStaticData,
  item => item,
);

export const detailSelectorFn = createSelector(detailSelector, item => item);

export const staticDataSelectorFn = createSelector(detailSelector, item => item.staticData);

export const seoDataFromStaticDataFn = createSelector(
  staticDataSelectorFn,
  item => item?.seo_markup_data || {},
);

export const rnrPageNumberSelectorFn = createSelector(detailSelector, item => item.page);

export const appliedFiltersSelector = createSelector(
  detailSelector,
  item => item.appliedFilters || [],
);

//For details page About property

export const aboutPropertySelectorFn = createSelector(
  aboutProperty,
  aboutProperty => aboutProperty,
);
// thunk selectors

export const similarHotelsThunkSelector = createSelector(
  [
    checkinSelectorFn,
    checkoutSelectorFn,
    roomStringSelectorFn,
    paxSplittedDataSelector,
    cityIdSelector,
    goingForwardPropertyIdSelector,
  ],
  (checkinObj, checkoutObj, roomstring, paxSplittedRoomString, cityId, hotelCode) => {
    return {
      checkinObj,
      checkoutObj,
      roomstring,
      paxSplittedRoomString,
      cityId,
      hotelCode,
    };
  },
);

export const hotelPolicyThunkSelector = createSelector(
  [
    checkinSelectorFn,
    checkoutSelectorFn,
    roomStringSelectorFn,
    paxSplittedDataSelector,
    cityIdSelector,
    goingForwardPropertyIdSelector,
    roomDataHermesSelector,
    oldPaxSelector,
    hotelPolicyFromHermesSelector,
  ],
  (
    checkinObj,
    checkoutObj,
    roomstring,
    paxSplittedRoomString,
    cityId,
    hotelCode,
    roomsDataHermes,
    oldPax,
    hotelPolicyFromHermesData,
  ) => {
    return {
      checkinObj,
      checkoutObj,
      roomstring,
      paxSplittedRoomString,
      cityId,
      hotelCode,
      roomsDataHermes,
      oldPax,
      hotelPolicyFromHermesData,
    };
  },
);

export const breadcrumbSelector = createSelector(
  [
    hotelArea,
    cityId,
    hotelCityURLSelectorFn,
    hotelName,
    checkinSelectorFn,
    checkoutSelectorFn,
    roomStringSelectorFn,
    countryUrlSelector,
    countryNameSelector,
  ],
  (
    cityName,
    cityId,
    hotelCityURL,
    hotelName,
    checkinObj,
    checkoutObj,
    roomString,
    countryUrl,
    countryName,
  ) => {
    const checkin = parseDateIntoHermesString(checkinObj.date);
    const checkout = parseDateIntoHermesString(checkoutObj.date);

    return {
      cityName,
      cityId,
      hotelCityURL,
      hotelName,
      checkin,
      checkout,
      roomString,
      countryUrl,
      countryName,
    };
  },
);

export const hotelInfoSelector = createSelector(
  [
    coupleFriendlyFlagSelectorFn,
    gostayFlagSelectorFn,
    gostaysHoverDataSelectorFn,
    coupleFriendlyDataSelectorFn,
  ],
  (isCF, isGostay, gostaysHoverList, cfData) => ({
    isCF,
    isGostay,
    gostaysHoverList,
    cfData,
  }),
);

export const reviewRatingSelector = createSelector(
  [hotelStaticRatingSelector, hotelRatingSourceSelector, hotelStaticReviewsNumberSelector],
  (hotelReviewRating, hotelRatingSource, numberOfReviews) => ({
    hotelReviewRating,
    hotelRatingSource,
    numberOfReviews,
  }),
);

export const locationPersuasionsSelector = createSelector(
  [locationPersuasions],
  locationPersuasions => locationPersuasions,
);

export const bookingWidgetSelector = createSelector(
  [
    roomsDataHermes,
    roomStringSelectorFn,
    isHermesLoading,
    hasHermesDataBooleanSelector,
    checkinSelectorFn,
    checkoutSelectorFn,
    hotelIdSelector,
    cityNameSelector,
    cityIdSelector,
    countrySelectorFn,
  ],
  (
    roomsDataFromHermes,
    roomString,
    isHermesLoading,
    hasHermesData,
    checkin,
    checkout,
    hotelId,
    cityName,
    cityId,
    country,
  ) => {
    return {
      roomsDataFromHermes,
      roomString,
      isHermesLoading,
      hasHermesData,
      checkin,
      checkout,
      hotelId,
      cityName,
      cityId,
      country,
    };
  },
);

export const hotelDetailsSoldOutStatus = createSelector(
  [isHermesLoading, hasHermesDataBooleanSelector, roomsDataHermes],
  (isHermesLoading, hasHermesData, roomsDataFromHermes) => {
    const isSoldOut = !isHermesLoading && !!hasHermesData && !roomsDataFromHermes.length;

    return isSoldOut;
  },
);

export const goSafePersuasionSelector = createSelector(
  hermesDataSelector,
  item => item?.data?.npp || [],
);

export const goSafeSegmentDataSelectorFn = createSelector(detailSelector, item => {
  let data = {};
  if (item?.staticData?.segment_block?.length) {
    const {
      cards = [],
      title = '',
      title_color: titleColor = '',
      footer: footerText = '',
      footer_color: footerColor,
      godata = {},
      captivate_images: goSafeCaptivateImages = {},
      title_bg_color: titleBackgroundColor = '',
      premium_prop_data: premiumPropData = {},
      persuation_title: persuasionTitle = '',
      card_type: cardType = '',
    } = item?.staticData?.segment_block[0];

    data = {
      cards,
      title,
      titleColor,
      footerText,
      footerColor,
      godata,
      goSafeCaptivateImages,
      titleBackgroundColor,
      premiumPropData,
      persuasionTitle,
      premiumPropStatus: cardType === 'premium_prop_data',
    };
  }
  return data;
});

export const showSafetyHygieneSectionSelector = createSelector(
  goSafeSegmentDataSelectorFn,
  item => {
    const {premiumPropData = {}} = item;
    const {heading = '', p_data = [], sub_heading: subHeading = ''} = premiumPropData;
    if (heading && p_data.length && subHeading) return true;
    return false;
  },
);

export const safetyHygieneMediaDataSelector = createSelector(
  detailSelector,
  item => item?.safetyHygieneMediaData || [],
);

export const preferredByDataSelector = createSelector(metaInfoSelector, metaInfo => {
  const {vps = []} = metaInfo;
  let data = vps.filter(item => item?.t && item?.tgs?.length);
  return data;
});
